/**
 * Component Detector Utility
 * Detects component information from clicked DOM elements
 */

/**
 * Extracts component/part information from a clicked element
 * @param {HTMLElement} element - The clicked DOM element
 * @returns {Object} Component information including tag, classes, text, and context
 */
const MAX_TRAVERSAL_DEPTH = 8;

const formatSegment = (el) => {
  if (!el || !el.tagName) return '';

  const tag = el.tagName.toLowerCase();

  if (el.id) {
    return `${tag}#${el.id}`;
  }

  const classList = Array.from(el.classList || []).filter(Boolean);
  const meaningfulClass = classList.find((cls) =>
    /section|container|wrapper|content|image|card/i.test(cls)
  );

  let segment = tag;
  if (meaningfulClass) {
    segment += `.${meaningfulClass}`;
  } else if (classList.length > 0) {
    segment += `.${classList[0]}`;
  }

  if (el.parentElement) {
    const siblings = Array.from(el.parentElement.children).filter(
      (child) => child.tagName === el.tagName
    );
    if (siblings.length > 1) {
      const index = siblings.indexOf(el);
      segment += `:nth-of-type(${index + 1})`;
    }
  }

  return segment;
};

const buildElementPath = (element) => {
  const segments = [];
  let current = element;
  let depth = 0;

  while (current && depth < MAX_TRAVERSAL_DEPTH) {
    if (current.hasAttribute?.('data-preview-container')) {
      segments.push('[preview-root]');
      break;
    }

    const segment = formatSegment(current);
    if (segment) {
      segments.push(segment);
    }

    current = current.parentElement;
    depth += 1;
  }

  return segments.reverse().join(' > ');
};

const findSectionDescriptor = (element) => {
  let current = element;
  let depth = 0;

  while (current && depth < MAX_TRAVERSAL_DEPTH) {
    if (current.dataset) {
      const sectionData =
        current.dataset.section ||
        current.dataset.segment ||
        current.dataset.block;
      if (sectionData) {
        return sectionData;
      }
    }

    const classList = Array.from(current.classList || []);
    const sectionClass = classList.find((cls) => /section|segment|block/i.test(cls));
    if (sectionClass) {
      return sectionClass;
    }

    if (current.tagName && current.tagName.toLowerCase() === 'section') {
      if (current.id) {
        return `section#${current.id}`;
      }
      if (classList.length > 0) {
        return `section.${classList[0]}`;
      }
      return 'section';
    }

    current = current.parentElement;
    depth += 1;
  }

  return '';
};

export const detectComponentInfo = (element) => {
  if (!element) {
    return {
      tagName: 'unknown',
      componentName: 'Unknown Component',
      className: '',
      textPreview: '',
      context: '',
      elementPath: '',
      section: '',
    };
  }

  // Walk up the DOM tree to find meaningful component information
  let currentElement = element;
  let depth = 0;
  const maxDepth = 5;
  
  let componentName = '';
  let className = '';
  let tagName = element.tagName?.toLowerCase() || 'div';
  let textPreview = '';
  let id = '';
  let dataAttributes = {};
  const elementPath = buildElementPath(element);
  const sectionDescriptor = findSectionDescriptor(element);

  // Collect data attributes
  Array.from(element.attributes || []).forEach(attr => {
    if (attr.name.startsWith('data-')) {
      dataAttributes[attr.name] = attr.value;
    }
  });

  // Try to find meaningful component information
  while (currentElement && depth < maxDepth) {
    // Check for React component name in class or id
    const classList = Array.from(currentElement.classList || []);
    
    // Get id if not already found
    if (!id && currentElement.id) {
      id = currentElement.id;
    }
    
    // Look for component-like class names (PascalCase or kebab-case)
    const componentClass = classList.find(cls => 
      /^[A-Z]/.test(cls) || cls.includes('component') || cls.includes('section')
    );
    
    if (componentClass && !componentName) {
      componentName = componentClass
        .replace(/-/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      className = componentClass;
    }

    // Check for data attributes that might indicate component type
    if (currentElement.dataset) {
      if (currentElement.dataset.component && !componentName) {
        componentName = currentElement.dataset.component
          .replace(/-/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      if (currentElement.dataset.section && !componentName) {
        componentName = currentElement.dataset.section
          .replace(/-/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
    }

    // Get text preview (first 100 chars)
    if (!textPreview && currentElement.textContent) {
      const text = currentElement.textContent.trim();
      if (text && text.length > 0) {
        textPreview = text.substring(0, 100);
        if (text.length > 100) textPreview += '...';
      }
    }

    // Move to parent
    currentElement = currentElement.parentElement;
    depth++;
  }

  // Fallback: use tag name if no component name found
  if (!componentName || componentName === '') {
    const tagMap = {
      'h1': 'Heading 1',
      'h2': 'Heading 2',
      'h3': 'Heading 3',
      'h4': 'Heading 4',
      'h5': 'Heading 5',
      'h6': 'Heading 6',
      'p': 'Paragraph',
      'div': 'Container',
      'span': 'Text Element',
      'img': 'Image',
      'button': 'Button',
      'a': 'Link',
      'section': 'Section',
      'article': 'Article',
      'header': 'Header',
      'footer': 'Footer',
    };
    componentName = tagMap[tagName] || tagName.charAt(0).toUpperCase() + tagName.slice(1);
  }

  // Build context string
  const contextParts = [];
  if (className) contextParts.push(`class: ${className}`);
  if (id) contextParts.push(`id: ${id}`);
  
  // Add data attributes to context
  Object.entries(dataAttributes).forEach(([key, value]) => {
    contextParts.push(`${key}: ${value}`);
  });

  return {
    tagName,
    componentName,
    className,
    textPreview,
    id,
    context: contextParts.join(', '),
    element: element,
    elementPath,
    section: sectionDescriptor,
  };
};

/**
 * Formats component information for chatbot message
 * @param {Object} componentInfo - Component information from detectComponentInfo
 * @param {string} userMessage - User's message
 * @returns {string} Formatted message with component context
 */
export const formatMessageWithContext = (componentInfo, userMessage = '') => {
  const { componentName, textPreview, context, elementPath, section } = componentInfo;
  
  let formattedMessage = `Regarding the "${componentName}" component`;
  
  // Add context if available and meaningful
  if (context && context.length > 0) {
    formattedMessage += ` (${context})`;
  }

  if (section) {
    formattedMessage += `\nSection hint: ${section}`;
  }

  if (elementPath) {
    formattedMessage += `\nDOM path: ${elementPath}`;
  }
  
  // Add text preview if available and not too long
  if (textPreview && textPreview.length > 0 && textPreview.length < 200) {
    formattedMessage += `\n\nContent: "${textPreview}"`;
  }
  
  // Add user message if provided
  if (userMessage && userMessage.trim().length > 0) {
    formattedMessage += `\n\n${userMessage}`;
  } else {
    // If no user message, add a default prompt
    formattedMessage += `\n\nPlease help me edit this component.`;
  }
  
  return formattedMessage;
};
